import React, { useState, useMemo, useRef, useEffect } from 'react';
import styles from './GearCategorySection.module.css';
import GearCard from './GearCard';
import { useTranslation } from 'react-i18next';

const sortOptions = [
  { value: 'default', labelKey: 'sortDefault' },
  { value: 'price-asc', labelKey: 'sortLowHigh' },
  { value: 'price-desc', labelKey: 'sortHighLow' }
];

const waterproofOptions = ['Yes', 'No', 'Partial'];

const getPriceValue = price => {
  if (typeof price === 'number' && Number.isFinite(price)) return price;
  if (typeof price === 'string') {
    const cleaned = price.replace(/[^0-9.,]/g, '').replace(',', '.');
    const parsed = parseFloat(cleaned);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return 0;
};

const getMaterialsList = (item) => {
  if (!item) return [];
  const rawMaterial = item.materials || item.material || item.material_type || item.upper_material || '';
  if (!rawMaterial) return [];
  if (Array.isArray(rawMaterial)) return rawMaterial;
  if (typeof rawMaterial === 'string') {
    try {
      const parsed = JSON.parse(rawMaterial);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {
      /* noop */
    }
    return rawMaterial.split(',').map(mat => mat.trim()).filter(Boolean);
  }
  return [];
};

const getBrandOptions = items => Array.from(new Set(
  items
    .map(item => (item.brand || '').trim())
    .filter(Boolean)
)).sort((a, b) => a.localeCompare(b));

const getMaterialOptions = items => Array.from(new Set(
  items.flatMap(item => getMaterialsList(item))
    .map(mat => mat.trim())
    .filter(Boolean)
)).sort((a, b) => a.localeCompare(b));

const getTypeOptions = items => Array.from(new Set(
  items.map(item => (item.baselayer_type || '').trim()).filter(Boolean)
)).sort((a, b) => a.localeCompare(b));

const getStyleOptions = items => Array.from(new Set(
  items.map(item => (item.baselayer_subtype || '').trim()).filter(Boolean)
)).sort((a, b) => a.localeCompare(b));

const getPointsOptions = items => Array.from(new Set(
  items.map(item => String(item.points || '').trim()).filter(Boolean)
)).sort((a, b) => {
  const numA = parseInt(a, 10);
  const numB = parseInt(b, 10);
  if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
  return a.localeCompare(b);
});

const getFrontPointsOptions = items => Array.from(new Set(
  items.map(item => (item.front_points || '').trim()).filter(Boolean)
)).sort((a, b) => a.localeCompare(b));

function GearCategorySection({ title, items, categoryType }) {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const filterPanelRef = useRef(null);
  const filterButtonRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      // If the row isn't scrollable, don't hijack the event
      if (el.scrollWidth <= el.clientWidth) return;

      // If the user is swiping horizontally on a trackpad,
      // let the browser handle it but stop the event from bubbling up
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.stopPropagation();
        return;
      }

      // If it's a vertical scroll (e.g. mouse wheel),
      // convert to horizontal and strictly prevent page-level scroll
      e.preventDefault();
      e.stopPropagation();
      el.scrollBy({
        left: e.deltaY,
        behavior: 'auto'
      });
    };

    // Use non-passive listener to ensure preventDefault() works reliably
    el.addEventListener('wheel', handleWheel, { passive: false });

    // Arrow visibility updater
    const updateArrows = () => {
      setShowLeftArrow(el.scrollLeft > 8);
      setShowRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    };

    updateArrows();
    el.addEventListener('scroll', updateArrows);
    window.addEventListener('resize', updateArrows);

    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, []);

  useEffect(() => {
    if (!showFilters) return undefined;

    const handleOutsideClick = (event) => {
      const panel = filterPanelRef.current;
      const button = filterButtonRef.current;
      if (!panel || !button) return;

      const target = event.target;
      if (panel.contains(target) || button.contains(target)) return;
      setShowFilters(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [showFilters]);
  const [filters, setFilters] = useState({
    brand: 'all',
    material: 'all',
    waterproof: 'all',
    sort: 'price-asc',
    baselayer_type: 'all',
    baselayer_subtype: 'all',
    points: 'all',
    front_points: 'all'
  });

  const brandOptions = useMemo(() => getBrandOptions(items), [items]);
  const materialOptions = useMemo(() => getMaterialOptions(items), [items]);
  const typeOptions = useMemo(() => getTypeOptions(items), [items]);
  const styleOptions = useMemo(() => getStyleOptions(items), [items]);
  const pointsOptions = useMemo(() => getPointsOptions(items), [items]);
  const frontPointsOptions = useMemo(() => getFrontPointsOptions(items), [items]);

  const availableSortOptions = useMemo(() => {
    const opts = [...sortOptions];
    const hasWaterproof = items.some(it => it && (it.waterproof != null || it.waterproofRating != null || it.waterColumn != null || it.water_resistance != null));
    const hasBreathability = items.some(it => it && (it.breathability != null || it.breathabilityRating != null || it.moistureVaporTransmission != null || it.mvp != null));
    const hasFill = items.some(it => it && (it.fillPower != null || it.downFill != null || it.fill_power != null));
    const hasWeight = items.some(it => it && (it.weight != null || it.grams != null || it.g != null));
    const hasFrontPoints = items.some(it => it && it.front_points != null && it.front_points !== '');

    if (hasWaterproof) {
      opts.push({ value: 'waterproof-desc', labelKey: 'sortWaterproofHigh' });
      opts.push({ value: 'waterproof-asc', labelKey: 'sortWaterproofLow' });
    }
    if (hasBreathability) {
      opts.push({ value: 'breathability-desc', labelKey: 'sortBreathabilityHigh' });
      opts.push({ value: 'breathability-asc', labelKey: 'sortBreathabilityLow' });
    }
    if (hasFill) {
      opts.push({ value: 'fillPower-desc', labelKey: 'sortFillPowerHigh' });
      opts.push({ value: 'fillPower-asc', labelKey: 'sortFillPowerLow' });
    }
    if (hasWeight) {
      opts.push({ value: 'weight-asc', labelKey: 'sortWeightLow' });
      opts.push({ value: 'weight-desc', labelKey: 'sortWeightHigh' });
    }
    const hasPoints = items.some(it => it && it.points != null && it.points !== '');
    if (hasPoints) {
      opts.push({ value: 'points-desc', labelKey: 'sortPointsHigh', fallback: 'Points (High to Low)' });
      opts.push({ value: 'points-asc', labelKey: 'sortPointsLow', fallback: 'Points (Low to High)' });
    }
    if (hasFrontPoints) {
      opts.push({ value: 'front_points-asc', labelKey: 'sortFrontPointsAsc', fallback: 'Front Points (A-Z)' });
      opts.push({ value: 'front_points-desc', labelKey: 'sortFrontPointsDesc', fallback: 'Front Points (Z-A)' });
    }

    return opts;
  }, [items]);

  // Determine which filters to show based on categoryType
  // categoryType can be: 'boots', 'backpacks', 'ice_axes', 'crampons', 'clothing'
  const showWaterproof = ['backpacks', 'clothing', 'boots'].includes(categoryType);
  const showMaterial = true; // Useful for almost everything including ice axes

  const filteredItems = useMemo(() => {
    let result = items.filter(item => {
      if (filters.brand !== 'all') {
        const itemBrand = (item.brand || '').toLowerCase();
        if (itemBrand !== filters.brand.toLowerCase()) return false;
      }

      if (filters.material !== 'all') {
        const materials = getMaterialsList(item).map(mat => mat.toLowerCase());
        if (!materials.includes(filters.material.toLowerCase())) return false;
      }

      if (showWaterproof && filters.waterproof !== 'all') {
        const wp = (item.waterproof || '').toLowerCase();
        // Handle "Partial" logic if needed, or exact match
        if (wp !== filters.waterproof.toLowerCase()) return false;
      }

      if (filters.baselayer_type !== 'all') {
        const t = (item.baselayer_type || '').toLowerCase();
        if (t !== filters.baselayer_type.toLowerCase()) return false;
      }

      if (filters.baselayer_subtype !== 'all') {
        const s = (item.baselayer_subtype || '').toLowerCase();
        if (s !== filters.baselayer_subtype.toLowerCase()) return false;
      }

      if (filters.points !== 'all') {
        const p = String(item.points || '').trim().toLowerCase();
        if (p !== String(filters.points).toLowerCase()) return false;
      }

      if (filters.front_points !== 'all') {
        const fp = String(item.front_points || '').trim().toLowerCase();
        if (fp !== String(filters.front_points).toLowerCase()) return false;
      }

      return true;
    });

    // Flexible sorting: support price and category-specific fields
    const parseNumberLike = (value) => {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string') {
        const cleaned = String(value).replace(/[^0-9.,-]/g, '').replace(',', '.');
        const parsed = parseFloat(cleaned);
        if (!Number.isNaN(parsed)) return parsed;
      }
      return null;
    };

    const getFieldValue = (item, keys) => {
      for (const k of keys) {
        if (item == null) continue;
        const v = item[k];
        if (v != null) return v;
      }
      return null;
    };

    const compareNumericField = (a, b, keys, asc = true) => {
      const va = parseNumberLike(getFieldValue(a, keys));
      const vb = parseNumberLike(getFieldValue(b, keys));
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return asc ? va - vb : vb - va;
    };

    const rankWaterproof = (val) => {
      if (val == null) return 0;
      const s = String(val).trim().toLowerCase();
      if (s === 'yes' || s === 'true') return 3;
      if (s === 'partial' || s === 'part' || s === 'some') return 2;
      if (s === 'no' || s === 'false') return 1;
      const num = parseNumberLike(val);
      return num != null ? num : 0;
    };

    if (filters.sort && filters.sort !== 'default') {
      switch (filters.sort) {
        case 'price-asc':
          result = [...result].sort((a, b) => getPriceValue(a.price) - getPriceValue(b.price));
          break;
        case 'price-desc':
          result = [...result].sort((a, b) => getPriceValue(b.price) - getPriceValue(a.price));
          break;
        case 'waterproof-desc':
          result = [...result].sort((a, b) => rankWaterproof(getFieldValue(b, ['waterproof', 'waterproofRating', 'waterColumn', 'water_resistance'])) - rankWaterproof(getFieldValue(a, ['waterproof', 'waterproofRating', 'waterColumn', 'water_resistance'])));
          break;
        case 'waterproof-asc':
          result = [...result].sort((a, b) => rankWaterproof(getFieldValue(a, ['waterproof', 'waterproofRating', 'waterColumn', 'water_resistance'])) - rankWaterproof(getFieldValue(b, ['waterproof', 'waterproofRating', 'waterColumn', 'water_resistance'])));
          break;
        case 'breathability-desc':
          result = [...result].sort((a, b) => compareNumericField(a, b, ['breathability', 'breathabilityRating', 'moistureVaporTransmission', 'mvp'], false));
          break;
        case 'breathability-asc':
          result = [...result].sort((a, b) => compareNumericField(a, b, ['breathability', 'breathabilityRating', 'moistureVaporTransmission', 'mvp'], true));
          break;
        case 'fillPower-desc':
          result = [...result].sort((a, b) => compareNumericField(a, b, ['fillPower', 'downFill', 'fill_power'], false));
          break;
        case 'fillPower-asc':
          result = [...result].sort((a, b) => compareNumericField(a, b, ['fillPower', 'downFill', 'fill_power'], true));
          break;
        case 'weight-asc':
          result = [...result].sort((a, b) => compareNumericField(a, b, ['weight', 'grams', 'g'], true));
          break;
        case 'weight-desc':
          result = [...result].sort((a, b) => compareNumericField(a, b, ['weight', 'grams', 'g'], false));
          break;
        case 'points-desc':
          result = [...result].sort((a, b) => compareNumericField(a, b, ['points'], false));
          break;
        case 'points-asc':
          result = [...result].sort((a, b) => compareNumericField(a, b, ['points'], true));
          break;
        case 'front_points-asc':
          result = [...result].sort((a, b) => String(a.front_points || '').localeCompare(String(b.front_points || '')));
          break;
        case 'front_points-desc':
          result = [...result].sort((a, b) => String(b.front_points || '').localeCompare(String(a.front_points || '')));
          break;
        default:
          break;
      }
    }

    return result;
  }, [items, filters, showWaterproof]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };



  if (items.length === 0) return null;

  return (
    <div className={styles.sectionContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        <div className={styles.controls}>
          <button
            className={`${styles.filterToggleButton} ${showFilters ? styles.filterToggleButtonActive : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            ref={filterButtonRef}
          >
            {t('gear.filters.title')}
            <span>{showFilters ? '-' : '+'}</span>
          </button>
        </div>
      </div>

      {showFilters && (
        <div className={styles.filterPanel} ref={filterPanelRef}>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>{t('gear.filters.brand')}</span>
            <select
              className={styles.filterSelect}
              value={filters.brand}
              onChange={e => handleFilterChange('brand', e.target.value)}
            >
              <option value="all">{t('gear.filters.brandAll')}</option>
              {brandOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </label>

          {showMaterial && (
            <label className={styles.filterField}>
              <span className={styles.filterLabel}>{t('gear.filters.material')}</span>
              <select
                className={styles.filterSelect}
                value={filters.material}
                onChange={e => handleFilterChange('material', e.target.value)}
              >
                <option value="all">{t('gear.filters.materialAll')}</option>
                {materialOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
          )}

          {showWaterproof && (
            <label className={styles.filterField}>
              <span className={styles.filterLabel}>{t('gear.filters.waterproof')}</span>
              <select
                className={styles.filterSelect}
                value={filters.waterproof}
                onChange={e => handleFilterChange('waterproof', e.target.value)}
              >
                <option value="all">{t('gear.filters.waterproofAll')}</option>
                {waterproofOptions.map(opt => (
                  <option key={opt} value={opt}>
                    {t(`uploadGear.options.waterproof.${opt}`)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {typeOptions.length > 0 && (
            <label className={styles.filterField}>
              <span className={styles.filterLabel}>{t('gear.filters.type', 'Type')}</span>
              <select
                className={styles.filterSelect}
                value={filters.baselayer_type}
                onChange={e => handleFilterChange('baselayer_type', e.target.value)}
              >
                <option value="all">{t('gear.filters.typeAll', 'All Types')}</option>
                {typeOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
          )}

          {styleOptions.length > 0 && (
            <label className={styles.filterField}>
              <span className={styles.filterLabel}>{t('gear.filters.style', 'Style')}</span>
              <select
                className={styles.filterSelect}
                value={filters.baselayer_subtype}
                onChange={e => handleFilterChange('baselayer_subtype', e.target.value)}
              >
                <option value="all">{t('gear.filters.styleAll', 'All Styles')}</option>
                {styleOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
          )}

          {pointsOptions.length > 0 && (
            <label className={styles.filterField}>
              <span className={styles.filterLabel}>{t('gear.filters.points', 'Points')}</span>
              <select
                className={styles.filterSelect}
                value={filters.points}
                onChange={e => handleFilterChange('points', e.target.value)}
              >
                <option value="all">{t('gear.filters.pointsAll', 'All Points')}</option>
                {pointsOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
          )}

          {frontPointsOptions.length > 0 && (
            <label className={styles.filterField}>
              <span className={styles.filterLabel}>{t('gear.filters.frontPoints', 'Front Points')}</span>
              <select
                className={styles.filterSelect}
                value={filters.front_points}
                onChange={e => handleFilterChange('front_points', e.target.value)}
              >
                <option value="all">{t('gear.filters.frontPointsAll', 'All Types')}</option>
                {frontPointsOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
          )}

          <label className={styles.filterField}>
            <span className={styles.filterLabel}>{t('gear.filters.sort')}</span>
            <select
              className={styles.filterSelect}
              value={filters.sort}
              onChange={e => handleFilterChange('sort', e.target.value)}
            >
              {availableSortOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.fallback ? t(`gear.filters.${opt.labelKey}`, opt.fallback) : t(`gear.filters.${opt.labelKey}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {filteredItems.length > 0 ? (
        <div className={styles.itemsRowWrapper}>
          {showLeftArrow && (
            <button
              className={`${styles.scrollArrow} ${styles.left}`}
              onClick={() => scrollRef.current && scrollRef.current.scrollBy({ left: -320, behavior: 'smooth' })}
              aria-label={t('gear.scrollLeft')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
          )}

          <div className={styles.itemsRow} ref={scrollRef}>
            {filteredItems.map(item => (
              <GearCard key={item.id} gear={item} />
            ))}
          </div>

          {showRightArrow && (
            <button
              className={`${styles.scrollArrow} ${styles.right}`}
              onClick={() => scrollRef.current && scrollRef.current.scrollBy({ left: 320, behavior: 'smooth' })}
              aria-label={t('gear.scrollRight')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          )}
        </div>
      ) : (
        <div className={styles.noGear}>
          {t('gear.noFiltered')}
        </div>
      )}
    </div>
  );
}

export default GearCategorySection;
